// src/components/character/creation/steps/BasicInfo.js
import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RefreshCw, Info } from 'lucide-react';
import { createRace, AVAILABLE_RACES } from '../../../../game-system/races';
import { generateCharacterName } from '../../../../services/nameGenerator';

const ALIGNMENTS = [
  { id: 'lg', name: 'Lawful Good', description: 'Believes in truth and justice, following the rules' },
  { id: 'ng', name: 'Neutral Good', description: 'Does what is good without bias for or against order' },
  { id: 'cg', name: 'Chaotic Good', description: 'Acts as their conscience directs, without regard for rules' },
  { id: 'ln', name: 'Lawful Neutral', description: 'Acts according to law, tradition, or personal codes' },
  { id: 'tn', name: 'True Neutral', description: 'Prefers to avoid taking sides in moral conflicts' },
  { id: 'cn', name: 'Chaotic Neutral', description: 'Follows their whims, valuing their own liberty' },
  { id: 'le', name: 'Lawful Evil', description: 'Methodically takes what they want within their code' },
  { id: 'ne', name: 'Neutral Evil', description: 'Does whatever they can get away with' },
  { id: 'ce', name: 'Chaotic Evil', description: 'Acts with arbitrary violence and destruction' }
];

export const BasicInfo = ({ character, onUpdate, onError, onSuccess }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedRace, setSelectedRace] = useState(character.race);
  const [selectedSubrace, setSelectedSubrace] = useState(character.subrace);

  // Get available subraces for selected race
  const getAvailableSubraces = useCallback((raceName) => {
    if (!raceName) return [];

    try {
      // Use the exported function which handles errors
      return AVAILABLE_RACES[raceName]?.availableSubraces || [];
    } catch (error) {
      console.error("Error getting subraces:", error);
      return [];
    }
  }, []);

  // Handle race selection
  const handleRaceChange = useCallback((e) => {
    const raceName = e.target.value;
    setSelectedRace(raceName);
    setSelectedSubrace(''); // Reset subrace when race changes

    try {
      onUpdate({
        race: raceName,
        subrace: '',
        // Reset racial bonuses and traits - just copy existing abilities
        abilities: { ...character.abilities }
      });
    } catch (error) {
      console.error("Error updating race:", error);
      onError("There was an error selecting this race");
    }
  }, [character.abilities, onUpdate, onError]);

  // Handle subrace selection
  const handleSubraceChange = useCallback((e) => {
    const subraceName = e.target.value;
    setSelectedSubrace(subraceName);

    if (selectedRace && subraceName) {
      try {
        // Create race instance
        const race = createRace(selectedRace, subraceName);

        onUpdate({
          subrace: subraceName,
          abilities: {
            ...character.abilities,
            // Since getAttributeBonuses might not exist, use attributeBonuses directly
            ...(race.attributeBonuses || {})
          }
        });
      } catch (error) {
        console.error("Error creating race:", error);
        onError("There was an error selecting this heritage");
      }
    }
  }, [selectedRace, character.abilities, onUpdate, onError]);

  // Handle name generation
  const handleGenerateName = async () => {
    if (!character.race || !character.alignment) {
      onError('Please select race and alignment first');
      return;
    }

    setIsGenerating(true);
    try {
      const generatedName = await generateCharacterName({
        race: character.race,
        subrace: character.subrace,
        background: character.background,
        alignment: character.alignment
      });

      onUpdate({ name: generatedName });
      onSuccess('A mystical name has been revealed!');
    } catch (error) {
      console.error('Name generation error:', error);
      onError('The naming ritual failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Description component for selections
  const SelectionDescription = ({ title, description }) => (
    <motion.div
      className="character-creation__description"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <h4 className="character-creation__selection-title">{title}</h4>
      <p>{description}</p>
    </motion.div>
  );

  return (
    <motion.div
      className="character-creation__content"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      {/* Name Section */}
      <div className="character-creation__form-group">
        <label className="character-creation__label">
          Mystical Name
          <span className="character-creation__required">*</span>
        </label>
        <div className="character-creation__input-group">
          <input
            type="text"
            className="character-creation__input"
            value={character.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="Enter your character's name"
            required
          />
          <button
            className="character-creation__button character-creation__button--secondary"
            onClick={handleGenerateName}
            disabled={isGenerating}
            type="button"
          >
            {isGenerating ? (
              <RefreshCw className="spin-animation" />
            ) : (
              <Sparkles />
            )}
            {isGenerating ? 'Channeling...' : 'Generate'}
          </button>
        </div>
      </div>

      {/* Race Selection */}
      <div className="character-creation__form-group">
        <label className="character-creation__label">
          Ancestral Race
          <span className="character-creation__required">*</span>
        </label>
        <select
          className="character-creation__select"
          value={selectedRace}
          onChange={handleRaceChange}
          required
        >
          <option value="">Choose your race</option>
          {Object.values(AVAILABLE_RACES).map((race) => (
            <option key={race.id} value={race.id}>
              {race.name}
            </option>
          ))}
        </select>
        
        <AnimatePresence mode="wait">
          {selectedRace && (
            <SelectionDescription
              title={AVAILABLE_RACES[selectedRace].name}
              description={AVAILABLE_RACES[selectedRace].description}
            />
          )}
        </AnimatePresence>

        {/* Subrace Selection */}
        {selectedRace && getAvailableSubraces(selectedRace).length > 0 && (
          <div className="character-creation__form-group">
            <label className="character-creation__label">
              Heritage
              <span className="character-creation__required">*</span>
            </label>
            <select
              className="character-creation__select"
              value={selectedSubrace}
              onChange={handleSubraceChange}
              required
            >
              <option value="">Choose your heritage</option>
              {getAvailableSubraces(selectedRace).map((subraceId) => (
                <option key={subraceId} value={subraceId}>
                  {subraceId.charAt(0).toUpperCase() + subraceId.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Background Selection */}
      <div className="character-creation__form-group">
        <label className="character-creation__label">
          Background
          <span className="character-creation__required">*</span>
        </label>
        <select
          className="character-creation__select"
          value={character.background}
          onChange={(e) => onUpdate({ background: e.target.value })}
          required
        >
          <option value="">Choose your background</option>
          <option value="scholar">Scholar</option>
          <option value="soldier">Soldier</option>
          <option value="merchant">Merchant</option>
          <option value="criminal">Criminal</option>
          <option value="healer">Healer</option>
          <option value="artisan">Artisan</option>
          <option value="explorer">Explorer</option>
          <option value="noble">Noble</option>
        </select>

        <AnimatePresence mode="wait">
          {character.background && (
            <SelectionDescription
              title={character.background.charAt(0).toUpperCase() + character.background.slice(1)}
              description="Your background represents your character's origin story and early life experiences."
            />
          )}
        </AnimatePresence>
      </div>

      {/* Alignment Selection */}
      <div className="character-creation__form-group">
        <label className="character-creation__label">
          Moral Alignment
          <span className="character-creation__required">*</span>
        </label>
        <select
          className="character-creation__select"
          value={character.alignment}
          onChange={(e) => onUpdate({ alignment: e.target.value })}
          required
        >
          <option value="">Choose your alignment</option>
          {ALIGNMENTS.map((alignment) => (
            <option key={alignment.id} value={alignment.id}>
              {alignment.name}
            </option>
          ))}
        </select>
        
        <AnimatePresence mode="wait">
          {character.alignment && (
            <SelectionDescription
              title={ALIGNMENTS.find(a => a.id === character.alignment)?.name}
              description={ALIGNMENTS.find(a => a.id === character.alignment)?.description}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Help Text */}
      <div className="character-creation__help-text">
        <Info size={20} />
        <p>
          Your choices here will shape your character's abilities and role in the world.
          Choose wisely, as these decisions will influence your adventure.
        </p>
      </div>

      {/* Racial Traits Preview */}
      {selectedRace && selectedSubrace && (
        <motion.div
          className="character-creation__selection"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h4 className="character-creation__selection-title">Racial Traits</h4>
          <div className="character-creation__traits-grid">
            {(() => {
              try {
                const race = createRace(selectedRace, selectedSubrace);
                // Handle case where getTraits() might not exist or not return expected format
                const traits = race.traits || [];

                return traits.map((trait, index) => (
                  <div key={index} className="character-creation__trait">
                    <h5>{trait.name}</h5>
                    <p>{trait.description}</p>
                  </div>
                ));
              } catch (error) {
                console.error("Error displaying traits:", error);
                return (
                  <div className="character-creation__trait">
                    <p>Racial traits information unavailable</p>
                  </div>
                );
              }
            })()}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default BasicInfo;